import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { speechLang } from "@/i18n";

const HINT_KEY = "vb.voiceHintSeen";
const hintSeen = () => { try { return Number(localStorage.getItem(HINT_KEY) || 0) >= 3; } catch { return true; } };

const Recognition = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

// Explicit tap-to-talk mic; inserts recognized text via onText. Hidden when unsupported.
export default function VoiceMic({ onText, testid = "voice-mic", className = "", hint = false, lang, showUnsupported = false, unsupportedMsg = "Voice input isn't supported in this browser — please type instead." }) {
  const [listening, setListening] = useState(false);
  const [showHint, setShowHint] = useState(() => hint && !hintSeen());
  const recRef = useRef(null);
  const { t } = useTranslation();
  const hintText = t("v.hint", { ex: t("v.example") });
  useEffect(() => {
    if (!showHint) return undefined;
    try { localStorage.setItem(HINT_KEY, String(Number(localStorage.getItem(HINT_KEY) || 0) + 1)); } catch { /* storage blocked: hint counter is optional */ }
    const id = setTimeout(() => setShowHint(false), 8000);
    return () => clearTimeout(id);
  }, [showHint]);
  if (!Recognition) {
    if (!showUnsupported) return null;
    return (
      <button type="button" onClick={(e) => { e.preventDefault(); toast.info(unsupportedMsg); }} aria-label="Voice input unavailable" title={unsupportedMsg}
        className={`flex-shrink-0 p-1.5 rounded-full text-slate-300 ${className}`} data-testid={`${testid}-unsupported`}>
        <MicOff className="w-4 h-4" />
      </button>
    );
  }

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (showHint) { setShowHint(false); try { localStorage.setItem(HINT_KEY, "3"); } catch { /* storage blocked: hint counter is optional */ } }
    if (listening) { recRef.current?.stop(); return; }
    const rec = new Recognition();
    rec.lang = lang || speechLang();
    rec.interimResults = false;
    rec.continuous = false;
    rec.maxAlternatives = 1;
    rec.onresult = (ev) => {
      const text = Array.from(ev.results).map((r) => r[0]?.transcript || "").join(" ").trim();
      if (text) onText(text);
    };
    rec.onerror = (ev) => {
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") toast.error("Microphone permission denied — please type instead.");
      else if (ev.error !== "no-speech" && ev.error !== "aborted") toast.error("Voice typing unavailable — please type instead.");
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch { setListening(false); }
  };

  const btn = (
    <button
      type="button" onClick={toggle} title={hint ? hintText : undefined} aria-label={listening ? "Stop voice typing" : "Voice typing"} aria-pressed={listening}
      className={`flex-shrink-0 p-1.5 rounded-full transition-colors ${listening ? "bg-rose-50 text-rose-600 animate-pulse" : "text-slate-400 hover:text-emerald-700"} ${className}`}
      data-testid={testid}
    >
      {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
    </button>
  );
  if (!hint) return btn;
  return (
    <span className="relative flex-shrink-0 inline-flex">
      {btn}
      {showHint && (
        <span className="pointer-events-none absolute top-full end-0 mt-1.5 z-20 whitespace-nowrap rounded-lg bg-slate-900 text-white text-[11px] font-medium px-2 py-1 shadow-lg" data-testid={`${testid}-hint`}>
          {hintText}
        </span>
      )}
    </span>
  );
}

export const appendText = (prev, text) => (prev && !/\s$/.test(prev) ? `${prev} ${text}` : `${prev || ""}${text}`);

export const MicRow = ({ onText, testid }) => (
  <div className="flex justify-end -mt-0.5"><VoiceMic onText={onText} testid={testid} /></div>
);
